// fetchAuth.ts - Completa las llamadas fetch() al servidor con la sesión y la empresa.
//
// Unas 70 llamadas de pantallas usaban fetch() directo con solo 'X-Tenant-ID'
// (que el servidor no reconoce) o sin token de sesión, y fallaban con 401/404.
// El cliente axios (api.ts) ya agrega Authorization y X-Tenant-Subdomain; esto
// hace lo mismo para fetch(), en un solo lugar. Solo toca peticiones al servidor
// propio y nunca reemplaza encabezados que la pantalla ya haya puesto.
import { auth } from '../config/firebase';
import { notifyIfAccountBlocked } from './accountStatus';

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');

/** Subdominio de la empresa según la dirección actual (misma regla que api.ts) */
export function tenantSubdomainFromHost(): string | null {
    if (import.meta.env.MODE === 'development') return 'demo';
    const hostname = window.location.hostname;
    // URL temporal de Vercel (*.vercel.app): la empresa por defecto es itobox
    const subdomain = hostname.includes('.vercel.app') ? 'itobox' : hostname.split('.')[0];
    return subdomain && !['www', 'localhost', 'sistemacourier'].includes(subdomain) ? subdomain : null;
}

function isOwnApi(url: string): boolean {
    if (url.startsWith('/api/')) return true;
    return !!API_BASE && url.startsWith(`${API_BASE}/api/`);
}

export function installFetchAuth(): void {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        if (!isOwnApi(url)) return originalFetch(input, init);

        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        try {
            // 'Bearer null' / 'Bearer undefined': pantallas que leían un token de
            // localStorage que nadie guarda (Marca Blanca, Onboarding, rastreo GPS)
            const current = headers.get('Authorization') || '';
            if (!current || /^Bearer\s*(null|undefined)?\s*$/i.test(current)) {
                await auth.authStateReady();
                const user = auth.currentUser;
                if (user) headers.set('Authorization', `Bearer ${await user.getIdToken()}`);
            }
            if (!headers.has('X-Tenant-Subdomain')) {
                const sub = tenantSubdomainFromHost();
                if (sub) headers.set('X-Tenant-Subdomain', sub);
            }
        } catch (e) {
            console.error('fetchAuth: no se pudo preparar la sesión', e);
        }
        const response = await originalFetch(input, { ...init, headers });
        if (response.status === 403) {
            response.clone().json().then(body => notifyIfAccountBlocked(403, body)).catch(() => {});
        }
        return response;
    };
}
